import DragButton from '@Components/UI/Buttons/EdDragButton/EdDragButton';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import EdCollapseButton from '@Components/UI/Buttons/EdCollapseButton/EdCollapseButton';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import EdAnimatedIcon from '@Components/UI/Utilities/EdAnimatedIcon/EdAnimatedIcon';
import EdFab from '@Components/UI/Utilities/EdFab/EdFab';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { useTypedSelector } from '@Features/store';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	IconButton,
	Paper,
	TextField,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexItem, FlexLayout, GridLayout, Spacer } from '@Styled/utilities';
import { lighten, transparentize } from 'polished';
import React, { useEffect, useRef, useState } from 'react';
import {
	DragDropContext,
	Draggable,
	DraggableProvidedDragHandleProps,
	Droppable,
	DropResult,
} from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import useStandalonePageLoader from './useStandalonePageLoader';

type Lecture = {
	id: number;
	name: string;
	code?: string;
	order: number;
	active: boolean;
};

type Section = {
	id: number;
	name: string;
	order: number;
	classroom_tab_id?: number;
	courses: Lecture[];
};

type ContentTab = {
	id: number;
	name: string;
	order: number;
	sections: Section[];
};

type StandaloneCourse = {
	id: number;
	label: string;
	tabs?: ContentTab[];
	// Lectures whose section was deleted (courses.section_id SET NULL) — not
	// reachable through tabs, served separately by the details endpoint.
	unsectioned_lectures?: Lecture[];
};

// tabIndex -1 marks an unsectioned ("-") lecture, which lives outside the
// tabs tree and refreshes via reload instead of local state surgery.
type ConfirmAction = {
	kind: 'tab' | 'section' | 'lecture';
	id: number;
	name: string;
	tabIndex: number;
	sectionIndex?: number;
	lectureIndex?: number;
};

const responseData = <T,>(response: any): T => response.data.data as T;

const StandaloneCourseCurriculum: React.FC<{
	course: StandaloneCourse;
	reload: () => void;
}> = ({ course, reload }) => {
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const auth = useTypedSelector((state) => state.auth);
	const isAdmin = api.defaults.baseURL?.includes('/admin/') === true;
	const permissions = (auth as any).permissions;
	// Curriculum authoring is admin-only — the instructor portal (instructors
	// and assistants) browses the structure without editing it; the backend
	// 403s these routes for instructors regardless.
	const canCreate =
		isAdmin && (auth.user.type === 'super' || permissions?.has('c-cur-cl'));
	const canUpdate =
		isAdmin && (auth.user.type === 'super' || permissions?.has('u-cur-cl'));
	const canDelete =
		isAdmin && (auth.user.type === 'super' || permissions?.has('d-cur-cl'));
	const [tabs, setTabs] = useState<ContentTab[]>(course.tabs || []);
	const [unsectioned, setUnsectioned] = useState<Lecture[]>(course.unsectioned_lectures || []);
	const [needReorder, setNeedReorder] = useState(false);
	const [confirmAction, setConfirmAction] = useState<ConfirmAction>();
	// One curriculum write at a time. A focused MUI Fab is a <button>, and the
	// browser fires a click for every keydown repeat while Enter is held — a
	// stuck key once put 5,498 sections on one course in three minutes. `busy`
	// disables the controls (which also drops them out of the focus order, so
	// the repeat chain dies), and the ref covers the gap before that state
	// lands: two keydowns in the same tick would both read the old `busy`.
	const inFlight = useRef(false);
	const [busy, setBusy] = useState(false);

	useEffect(() => setTabs(course.tabs || []), [course.tabs]);
	useEffect(() => setUnsectioned(course.unsectioned_lectures || []), [course.unsectioned_lectures]);

	// Returns undefined when a write is already in flight — callers must bail
	// rather than apply their local state change, or the UI would show an edit
	// the server never received.
	const request = async (
		method: 'post' | 'patch' | 'put' | 'delete',
		url: string,
		data: any,
		message: string
	) => {
		if (inFlight.current) return undefined;
		inFlight.current = true;
		setBusy(true);
		try {
			const response = await withLoading(() =>
				api.request({ method, url, data })
			);
			displaySnackbar('success', message);
			return response;
		} catch (error: any) {
			displaySnackbar(
				'error',
				error.response?.data?.message || 'Unable to update curriculum'
			);
			throw error;
		} finally {
			inFlight.current = false;
			setBusy(false);
		}
	};

	const addTab = async () => {
		const response = await request(
			'post',
			'standalone-courses/tabs/create',
			{
				standalone_course_id: course.id,
				name: `Tab ${tabs.length + 1} name`,
			},
			'Tab created successfully'
		);
		if (!response) return;
		const created = responseData<ContentTab>(response);
		setTabs((current) => [...current, { ...created, sections: [] }]);
	};

	const updateTab = async (tabIndex: number, name: string) => {
		const tab = tabs[tabIndex];
		const response = await request(
			'patch',
			'standalone-courses/tabs/update',
			// name only: position is owned by Save Order
			{ id: tab.id, name },
			'Edited Tab Successfully'
		);
		if (!response) return;
		setTabs((current) =>
			current.map((item, index) => (index === tabIndex ? { ...item, name } : item))
		);
	};

	const addSection = async (tabIndex: number) => {
		const tab = tabs[tabIndex];
		const response = await request(
			'post',
			'standalone-courses/sections/create',
			{
				standalone_course_id: course.id,
				classroom_tab_id: tab.id,
				name: `Section ${tab.sections.length + 1}`,
			},
			'Section created successfully'
		);
		if (!response) return;
		const created = responseData<Section>(response);
		setTabs((current) =>
			current.map((item, index) =>
				index === tabIndex
					? { ...item, sections: [...item.sections, { ...created, courses: [] }] }
					: item
			)
		);
	};

	const updateSection = async (
		tabIndex: number,
		sectionIndex: number,
		name: string
	) => {
		const tab = tabs[tabIndex];
		const section = tab.sections[sectionIndex];
		const response = await request(
			'patch',
			'standalone-courses/sections/update',
			// name only: after an unsaved drag these carried the NEW tab with the
			// OLD order, persisting half of the move
			{ id: section.id, name },
			'Edited section successfully'
		);
		if (!response) return;
		setTabs((current) =>
			current.map((item, index) =>
				index === tabIndex
					? {
							...item,
							sections: item.sections.map((currentSection, currentIndex) =>
								currentIndex === sectionIndex
									? { ...currentSection, name }
									: currentSection
							),
						}
					: item
			)
		);
	};

	const addLecture = async (
		tabIndex: number,
		sectionIndex: number,
		name: string,
		code: string
	) => {
		const section = tabs[tabIndex].sections[sectionIndex];
		const response = await request(
			'post',
			'standalone-courses/lectures/create',
			{
				standalone_course_id: course.id,
				section_id: section.id,
				name,
				code,
				description: '',
				order: section.courses.length + 1,
				active: true,
			},
			'Lecture created successfully'
		);
		if (!response) return;
		const created = responseData<Lecture>(response);
		setTabs((current) =>
			current.map((tab, currentTabIndex) =>
				currentTabIndex === tabIndex
					? {
							...tab,
							sections: tab.sections.map((currentSection, currentSectionIndex) =>
								currentSectionIndex === sectionIndex
									? {
											...currentSection,
											courses: [...currentSection.courses, created],
										}
									: currentSection
							),
						}
					: tab
			)
		);
	};

	const updateLecture = async (
		tabIndex: number,
		sectionIndex: number,
		lectureIndex: number,
		name: string,
		code: string
	) => {
		const lecture = tabs[tabIndex].sections[sectionIndex].courses[lectureIndex];
		const response = await request(
			'put',
			'standalone-courses/lectures/update',
			{ id: lecture.id, name, code },
			'Lecture updated successfully'
		);
		if (!response) return;
		setTabs((current) =>
			current.map((tab, currentTabIndex) =>
				currentTabIndex === tabIndex
					? {
							...tab,
							sections: tab.sections.map((section, currentSectionIndex) =>
								currentSectionIndex === sectionIndex
									? {
											...section,
											courses: section.courses.map((currentLecture, currentLectureIndex) =>
												currentLectureIndex === lectureIndex
													? { ...currentLecture, name, code }
													: currentLecture
											),
										}
									: section
							),
						}
					: tab
			)
		);
	};

	const updateUnsectionedLecture = async (
		lectureIndex: number,
		name: string,
		code: string
	) => {
		const lecture = unsectioned[lectureIndex];
		const response = await request(
			'put',
			'standalone-courses/lectures/update',
			{ id: lecture.id, name, code },
			'Lecture updated successfully'
		);
		if (!response) return;
		setUnsectioned((current) =>
			current.map((currentLecture, currentIndex) =>
				currentIndex === lectureIndex
					? { ...currentLecture, name, code }
					: currentLecture
			)
		);
	};

	const deleteConfirmed = async (_open: boolean, confirm?: boolean) => {
		if (!confirm || !confirmAction) {
			setConfirmAction(undefined);
			return;
		}
		const endpoint =
			confirmAction.kind === 'tab'
				? 'standalone-courses/tabs/delete'
				: confirmAction.kind === 'section'
				? 'standalone-courses/sections/delete'
				: 'standalone-courses/lectures/delete';
		const response = await request(
			'delete',
			endpoint,
			{ id: confirmAction.id },
			`${confirmAction.name} deleted successfully`
		);
		if (!response) return;
		if (confirmAction.tabIndex === -1) {
			setUnsectioned((current) => current.filter((lecture) => lecture.id !== confirmAction.id));
			setConfirmAction(undefined);
			return;
		}
		setTabs((current) => {
			if (confirmAction.kind === 'tab') {
				return current.filter((_, index) => index !== confirmAction.tabIndex);
			}
			return current.map((tab, tabIndex) => {
				if (tabIndex !== confirmAction.tabIndex) return tab;
				if (confirmAction.kind === 'section') {
					return {
						...tab,
						sections: tab.sections.filter(
							(_, index) => index !== confirmAction.sectionIndex
						),
					};
				}
				return {
					...tab,
					sections: tab.sections.map((section, sectionIndex) =>
						sectionIndex === confirmAction.sectionIndex
							? {
									...section,
									courses: section.courses.filter(
										(_, index) => index !== confirmAction.lectureIndex
									),
								}
							: section
					),
				};
			});
		});
		// a deleted tab/section leaves its lectures section-less server-side
		// (SET NULL): reload so they show under the unsectioned list
		if (confirmAction.kind !== 'lecture') reload();
		setConfirmAction(undefined);
	};

	const cloneTabs = () =>
		tabs.map((tab) => ({
			...tab,
			sections: tab.sections.map((section) => ({
				...section,
				courses: [...section.courses],
			})),
		}));

	const onDragEnd = ({ source, destination, type }: DropResult) => {
		// view-only portals can start a drag, but it must never take: the
		// save-order button they'd need is hidden, so a taken drag would strand
		// a phantom local order
		if (!canUpdate) return;
		if (!destination) return;
		const next = cloneTabs();
		if (type === 'TABS') {
			next.splice(destination.index, 0, next.splice(source.index, 1)[0]);
		} else if (type === 'SECTIONS') {
			const sourceTabIndex = Number(source.droppableId.split('-')[1]);
			const destinationTabIndex = Number(destination.droppableId.split('-')[1]);
			const [section] = next[sourceTabIndex].sections.splice(source.index, 1);
			next[destinationTabIndex].sections.splice(destination.index, 0, section);
		} else if (type === 'LECTURES') {
			const [, sourceTabIndex, , sourceSectionIndex] = source.droppableId.split('-').map(Number);
			const [, destinationTabIndex, , destinationSectionIndex] = destination.droppableId.split('-').map(Number);
			const [lecture] = next[sourceTabIndex].sections[sourceSectionIndex].courses.splice(source.index, 1);
			next[destinationTabIndex].sections[destinationSectionIndex].courses.splice(destination.index, 0, lecture);
		}
		setTabs(next);
		setNeedReorder(true);
	};

	const saveOrder = async () => {
		const response = await request(
			'post',
			'standalone-courses/curriculum/update',
			{
				standalone_course_id: course.id,
				tabs: tabs.map((tab, tabIndex) => ({
					...tab,
					order: tabIndex + 1,
					sections: tab.sections.map((section, sectionIndex) => ({
						...section,
						classroom_tab_id: tab.id,
						order: sectionIndex + 1,
						courses: section.courses.map((lecture, lectureIndex) => ({
							...lecture,
							section_id: section.id,
							order: lectureIndex + 1,
						})),
					})),
				})),
			},
			'Curriculum re-ordered successfully'
		);
		if (!response) return;
		setNeedReorder(false);
		await reload();
	};

	return (
		<PageWrapper>
			<EdConfirmModal
				open={Boolean(confirmAction)}
				handleClose={deleteConfirmed}
				text={`Are you sure to delete ${confirmAction?.name || 'this item'}?`}
			/>
			{canCreate && (
				<FlexLayout alignItems="center">
					<EdFab onClick={addTab} disabled={busy} size="small" edbackgroundColor="purple">
						<EdIcon fontSize="medium">add</EdIcon>
					</EdFab>
					<Spacer mx="0.5rem" />
					<FabButtonLabel>Add Tab</FabButtonLabel>
				</FlexLayout>
			)}
			<Spacer mb="2.25rem" />
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="TABS" type="TABS">
					{({ droppableProps, innerRef, placeholder }, { isDraggingOver }) => (
						<TabDroppable
							{...droppableProps}
							ref={innerRef}
							$isDraggingOver={isDraggingOver}
						>
							{tabs.map((tab, tabIndex) => (
								<Draggable key={tab.id} draggableId={`TAB-${tab.id}`} index={tabIndex}>
									{({ draggableProps, dragHandleProps, innerRef: tabRef }, { isDragging }) => (
										<div ref={tabRef} {...draggableProps}>
											<TabPanel
												tab={tab}
												tabIndex={tabIndex}
												dragHandleProps={dragHandleProps}
												isDragging={isDragging}
												canCreate={canCreate}
												canUpdate={canUpdate}
												canDelete={canDelete}
												busy={busy}
												onUpdate={updateTab}
												onAddSection={addSection}
												onUpdateSection={updateSection}
												onAddLecture={addLecture}
												onUpdateLecture={updateLecture}
												onDelete={setConfirmAction}
												onContent={(lectureId) =>
													history.push(
														`/dashboard/classrooms/standalone-courses/${course.label}/lecture/${lectureId}/edit/content`
													)
												}
											/>
										</div>
									)}
								</Draggable>
							))}
							{placeholder}
						</TabDroppable>
					)}
				</Droppable>
			</DragDropContext>
			{unsectioned.length > 0 && (
				<UnsectionedPanel>
					<FlexLayout alignItems="center">
						<SectionTitle>Section -</SectionTitle>
						<UnsectionedHint>
							Lectures whose section was deleted — open Content to assign them
							to a section
						</UnsectionedHint>
					</FlexLayout>
					{unsectioned.map((lecture, lectureIndex) => (
						<LectureRow
							key={lecture.id}
							lecture={lecture}
							canUpdate={canUpdate}
							canDelete={canDelete}
							onUpdate={(name, code) =>
								updateUnsectionedLecture(lectureIndex, name, code)
							}
							onDelete={() =>
								setConfirmAction({
									kind: 'lecture',
									id: lecture.id,
									name: lecture.name,
									tabIndex: -1,
								})
							}
							onContent={() =>
								history.push(
									`/dashboard/classrooms/standalone-courses/${course.label}/lecture/${lecture.id}/edit/content`
								)
							}
						/>
					))}
				</UnsectionedPanel>
			)}
			{canUpdate && needReorder && (
				<FlexLayout justifyContent="flex-end" width="100%">
					<Button
						onClick={() => {
							// the saved tree, not the last-loaded snapshot (which lost
							// anything created / renamed / deleted since)
							setNeedReorder(false);
							reload();
						}}
						variant="contained"
						color="warning"
					>
						Cancel
					</Button>
					<Spacer mx="1rem" />
					<Button onClick={saveOrder} disabled={busy} variant="contained">
						Save Order
					</Button>
				</FlexLayout>
			)}
		</PageWrapper>
	);
};

const TabPanel: React.FC<{
	tab: ContentTab;
	tabIndex: number;
	dragHandleProps?: DraggableProvidedDragHandleProps | null;
	isDragging: boolean;
	canCreate: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	/** A curriculum write is in flight — every create control is inert. */
	busy: boolean;
	onUpdate: (tabIndex: number, name: string) => void;
	onAddSection: (tabIndex: number) => void;
	onUpdateSection: (tabIndex: number, sectionIndex: number, name: string) => void;
	onAddLecture: (tabIndex: number, sectionIndex: number, name: string, code: string) => void;
	onUpdateLecture: (tabIndex: number, sectionIndex: number, lectureIndex: number, name: string, code: string) => void;
	onDelete: (action: ConfirmAction) => void;
	onContent: (lectureId: number) => void;
}> = ({
	tab,
	tabIndex,
	dragHandleProps,
	isDragging,
	canCreate,
	canUpdate,
	canDelete,
	busy,
	onUpdate,
	onAddSection,
	onUpdateSection,
	onAddLecture,
	onUpdateLecture,
	onDelete,
	onContent,
}) => {
	const [opened, setOpened] = useState(false);
	const [name, setName] = useState(tab.name);
	useEffect(() => setName(tab.name), [tab.name]);
	return (
		<StyledTabAccordion
			expanded={opened}
			$isDragging={isDragging}
			TransitionProps={{ unmountOnExit: true }}
		>
			<AccordionSummary disableRipple disableTouchRipple>
				<FlexLayout width="100%">
					<FlexItem flex="1">
						<EdCollapseButton collapsed={!opened} onClick={() => setOpened(!opened)} />
						<FlexLayout alignItems="center">
							{dragHandleProps && <DragButton dragHandlers={dragHandleProps} />}
							<TabTitle>Tab {tabIndex + 1}</TabTitle>
							<TextField
								value={name}
								onChange={(event) => setName(event.target.value)}
								size="small"
								disabled={!canUpdate}
							/>
						</FlexLayout>
					</FlexItem>
					<FlexItem>
						{canDelete && (
							<IconButton
								onClick={() =>
									onDelete({ kind: 'tab', id: tab.id, name: tab.name, tabIndex })
								}
							>
								<EdIcon>delete</EdIcon>
							</IconButton>
						)}
						{name !== tab.name && canUpdate && (
							<IconButton onClick={() => onUpdate(tabIndex, name)} color="warning">
								<EdAnimatedIcon animation="shake">
									<EdIcon>check_circle_outline</EdIcon>
								</EdAnimatedIcon>
							</IconButton>
						)}
					</FlexItem>
				</FlexLayout>
			</AccordionSummary>
			<AccordionDetails>
				{canCreate && (
					<FlexLayout alignItems="center">
						<EdFab
							onClick={() => onAddSection(tabIndex)}
							disabled={busy}
							size="small"
							color="primary"
						>
							<EdIcon fontSize="medium">add</EdIcon>
						</EdFab>
						<Spacer mx="0.5rem" />
						<FabButtonLabel>Add Section</FabButtonLabel>
					</FlexLayout>
				)}
				<Droppable type="SECTIONS" droppableId={`TAB-${tabIndex}`}>
					{({ droppableProps, innerRef, placeholder }, { isDraggingOver }) => (
						<SectionDroppable
							{...droppableProps}
							ref={innerRef}
							$isDraggingOver={isDraggingOver}
						>
							{tab.sections.map((section, sectionIndex) => (
								<Draggable
									key={section.id}
									draggableId={`SECTION-${section.id}`}
									index={sectionIndex}
								>
									{({ draggableProps, dragHandleProps, innerRef: sectionRef }, state) => (
										<div ref={sectionRef} {...draggableProps}>
											<SectionPanel
												section={section}
												tabIndex={tabIndex}
												sectionIndex={sectionIndex}
												dragHandleProps={dragHandleProps}
												isDragging={state.isDragging}
												canCreate={canCreate}
												canUpdate={canUpdate}
												canDelete={canDelete}
												busy={busy}
												onUpdate={onUpdateSection}
												onAddLecture={onAddLecture}
												onUpdateLecture={onUpdateLecture}
												onDelete={onDelete}
												onContent={onContent}
											/>
										</div>
									)}
								</Draggable>
							))}
							{placeholder}
						</SectionDroppable>
					)}
				</Droppable>
			</AccordionDetails>
		</StyledTabAccordion>
	);
};

const SectionPanel: React.FC<{
	section: Section;
	tabIndex: number;
	sectionIndex: number;
	dragHandleProps?: DraggableProvidedDragHandleProps | null;
	isDragging: boolean;
	canCreate: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	/** A curriculum write is in flight — every create control is inert. */
	busy: boolean;
	onUpdate: (tabIndex: number, sectionIndex: number, name: string) => void;
	onAddLecture: (tabIndex: number, sectionIndex: number, name: string, code: string) => void;
	onUpdateLecture: (tabIndex: number, sectionIndex: number, lectureIndex: number, name: string, code: string) => void;
	onDelete: (action: ConfirmAction) => void;
	onContent: (lectureId: number) => void;
}> = ({
	section,
	tabIndex,
	sectionIndex,
	dragHandleProps,
	isDragging,
	canCreate,
	canUpdate,
	canDelete,
	busy,
	onUpdate,
	onAddLecture,
	onUpdateLecture,
	onDelete,
	onContent,
}) => {
	const [opened, setOpened] = useState(false);
	const [name, setName] = useState(section.name);
	const [addingLecture, setAddingLecture] = useState(false);
	const [lectureName, setLectureName] = useState('');
	const [lectureCode, setLectureCode] = useState('');
	useEffect(() => setName(section.name), [section.name]);
	const confirmLecture = async () => {
		await onAddLecture(tabIndex, sectionIndex, lectureName, lectureCode);
		setLectureName('');
		setLectureCode('');
		setAddingLecture(false);
	};
	return (
		<StyledSectionAccordion
			expanded={opened}
			$isDragging={isDragging}
			TransitionProps={{ unmountOnExit: true }}
		>
			<AccordionSummary>
				<EdCollapseButton collapsed={!opened} onClick={() => setOpened(!opened)} />
				<FlexLayout alignItems="center">
					<SectionTitle>Section {sectionIndex + 1}</SectionTitle>
					<SectionSummaryPaper>
						<FlexLayout alignItems="center">
							{dragHandleProps && <DragButton dragHandlers={dragHandleProps} />}
							<Spacer mx="0.5rem" />
							<TextField
								fullWidth
								size="small"
								value={name}
								onChange={(event) => setName(event.target.value)}
								disabled={!canUpdate}
							/>
						</FlexLayout>
					</SectionSummaryPaper>
					{name !== section.name && canUpdate && (
						<IconButton onClick={() => onUpdate(tabIndex, sectionIndex, name)} color="warning">
							<EdAnimatedIcon animation="shake">
								<EdIcon>check_circle_outline</EdIcon>
							</EdAnimatedIcon>
						</IconButton>
					)}
					{canDelete && (
						<IconButton
							onClick={() =>
								onDelete({
									kind: 'section',
									id: section.id,
									name: section.name,
									tabIndex,
									sectionIndex,
								})
							}
						>
							<EdIcon>delete</EdIcon>
						</IconButton>
					)}
				</FlexLayout>
			</AccordionSummary>
			<AccordionDetails>
				<GridLayout gridTemplateColumns="15% 1fr">
					<FlexLayout alignItems="center">
						<EdFab
							disabled={addingLecture || busy || !canCreate}
							onClick={() => setAddingLecture(true)}
							size="small"
							edbackgroundColor="orange"
						>
							<EdIcon fontSize="medium">add</EdIcon>
						</EdFab>
						<Spacer mx="0.5rem" />
						<FabButtonLabel>Add Lecture</FabButtonLabel>
					</FlexLayout>
					{addingLecture && (
						<LectureFormPaper>
							<FlexLayout alignItems="center" gridGap="1rem">
								<EdIcon>height</EdIcon>
								<FlexLayout minWidth="15rem">
									<TextField
										fullWidth
										size="small"
										placeholder="Lecture name"
										value={lectureName}
										onChange={(event) => setLectureName(event.target.value)}
									/>
								</FlexLayout>
								<FlexLayout maxWidth="10rem">
									<TextField
										size="small"
										placeholder="Code"
										value={lectureCode}
										onChange={(event) => setLectureCode(event.target.value)}
									/>
								</FlexLayout>
							</FlexLayout>
							{lectureName.trim() && lectureCode.trim() ? (
								<IconButton onClick={confirmLecture} disabled={busy}>
									<EdAnimatedIcon animation="shake">
										<EdIcon>check_circle_outline</EdIcon>
									</EdAnimatedIcon>
								</IconButton>
							) : null}
						</LectureFormPaper>
					)}
				</GridLayout>
				<Droppable
					type="LECTURES"
					droppableId={`TAB-${tabIndex}-SECTION-${sectionIndex}`}
				>
					{({ droppableProps, innerRef, placeholder }, { isDraggingOver }) => (
						<LectureDroppable
							{...droppableProps}
							ref={innerRef}
							$isDraggingOver={isDraggingOver}
						>
							{section.courses.map((lecture, lectureIndex) => (
								<Draggable
									key={lecture.id}
									draggableId={`LECTURE-${lecture.id}`}
									index={lectureIndex}
								>
									{({ draggableProps, dragHandleProps, innerRef: lectureRef }) => (
										<div ref={lectureRef} {...draggableProps}>
											<LectureRow
												lecture={lecture}
												dragHandleProps={dragHandleProps}
												canUpdate={canUpdate}
												canDelete={canDelete}
												onUpdate={(name, code) =>
													onUpdateLecture(
														tabIndex,
														sectionIndex,
														lectureIndex,
														name,
														code
													)
												}
												onDelete={() =>
													onDelete({
														kind: 'lecture',
														id: lecture.id,
														name: lecture.name,
														tabIndex,
														sectionIndex,
														lectureIndex,
													})
												}
												onContent={() => onContent(lecture.id)}
											/>
										</div>
									)}
								</Draggable>
							))}
							{placeholder}
						</LectureDroppable>
					)}
				</Droppable>
			</AccordionDetails>
		</StyledSectionAccordion>
	);
};

const LectureRow: React.FC<{
	lecture: Lecture;
	dragHandleProps?: DraggableProvidedDragHandleProps | null;
	canUpdate: boolean;
	canDelete: boolean;
	onUpdate: (name: string, code: string) => void;
	onDelete: () => void;
	onContent: () => void;
}> = ({ lecture, dragHandleProps, canUpdate, canDelete, onUpdate, onDelete, onContent }) => {
	const [name, setName] = useState(lecture.name);
	const [code, setCode] = useState(lecture.code || '');
	useEffect(() => {
		setName(lecture.name);
		setCode(lecture.code || '');
	}, [lecture.code, lecture.name]);
	const dirty = name !== lecture.name || code !== (lecture.code || '');
	return (
		<FlexLayout justifyContent="space-between" alignItems="center">
			<LectureFormPaper>
				<FlexLayout alignItems="center" gridGap="1rem">
					{dragHandleProps && <DragButton dragHandlers={dragHandleProps} />}
					<FlexLayout minWidth="15rem">
						<TextField
							fullWidth
							size="small"
							value={name}
							onChange={(event) => setName(event.target.value)}
							disabled={!canUpdate}
						/>
					</FlexLayout>
					<FlexLayout maxWidth="10rem">
						<TextField
							size="small"
							value={code}
							onChange={(event) => setCode(event.target.value)}
							disabled={!canUpdate}
						/>
					</FlexLayout>
					<EdButton
						minWidth="8rem"
						onClick={onContent}
						variant="outlined"
						edcolor="cadet"
						startIcon={<EdIcon>add</EdIcon>}
					>
						Content
					</EdButton>
					{dirty && canUpdate && name.trim() && code.trim() ? (
						<IconButton onClick={() => onUpdate(name, code)}>
							<EdAnimatedIcon animation="shake">
								<EdIcon>edit</EdIcon>
							</EdAnimatedIcon>
						</IconButton>
					) : null}
				</FlexLayout>
			</LectureFormPaper>
			{canDelete && (
				<IconButton onClick={onDelete}>
					<EdIcon>delete</EdIcon>
				</IconButton>
			)}
		</FlexLayout>
	);
};

const PageWrapper = styled.div`
	padding: 0 6.25rem;
`;

const FabButtonLabel = styled.label`
	color: #adadad;
	font-size: 0.875rem;
`;

const TabTitle = styled.label`
	font-size: 0.875rem;
	margin: 0 3.75rem 0 1rem;
`;

const SectionTitle = styled.label`
	font-size: 0.875rem;
	min-width: 7.125rem;
	margin: 0;
`;

const StyledTabAccordion = styled(Accordion)<{ $isDragging?: boolean }>`
	margin: 2rem 0;
	background-color: ${(props) =>
		props.$isDragging
			? lighten(0.25, props.theme.palette.purple?.main ?? '')
			: grey[100]};
	.MuiAccordionSummary-root.Mui-focusVisible {
		background-color: ${grey[100]};
	}
	&.Mui-expanded:last-of-type {
		margin: 2rem 0;
	}
`;

const StyledSectionAccordion = styled(Accordion)<{ $isDragging?: boolean }>`
	margin: 1rem 0;
	padding: 0.5rem 0 1rem 0.406rem;
	border: ${(props) =>
		props.$isDragging ? 'none' : `1px solid ${props.theme.palette.grey[600]}`};
	background-color: ${(props) =>
		props.$isDragging
			? lighten(0.25, props.theme.palette.primary?.main ?? '')
			: grey[100]};
	box-shadow: none;
	.MuiAccordionSummary-root {
		padding: 0;
	}
	&.Mui-expanded:last-of-type {
		margin: 1rem 0;
	}
`;

const SectionSummaryPaper = styled(Paper)`
	padding: 1rem;
	min-width: 27rem;
	margin: 0 0.5rem;
`;

const UnsectionedPanel = styled.div`
	margin: 1rem 0;
	padding: 0.5rem 1rem 1rem 0.406rem;
	border: 1px dashed ${(props) => props.theme.palette.grey[600]};
	border-radius: 4px;
	background-color: ${grey[100]};
`;

const UnsectionedHint = styled.label`
	color: #adadad;
	font-size: 0.875rem;
`;

const LectureFormPaper = styled(Paper)`
	padding: 0.375rem 1rem;
	margin: 0.625rem 0;
	display: flex;
	justify-content: space-between;
	flex: 1;
`;

const TabDroppable = styled.div<{ $isDraggingOver?: boolean }>`
	padding: ${(props) => (props.$isDraggingOver ? '1rem' : '')};
	border-radius: 5px;
	border: ${(props) =>
		props.$isDraggingOver ? `1px dashed ${transparentize(0.9, '#000')}` : ''};
	background-color: ${(props) =>
		props.$isDraggingOver
			? transparentize(0.95, props.theme.palette.purple?.main ?? '')
			: ''};
`;

const SectionDroppable = styled.div<{ $isDraggingOver?: boolean }>`
	/* never 0 high: an empty tab must still be a drop target */
	min-height: ${(props) => (props.$isDraggingOver ? '10vh' : '3rem')};
	height: fit-content;
	margin: 0.5rem 0;
	padding: ${(props) => (props.$isDraggingOver ? '1rem' : '')};
	border-radius: 5px;
	border: ${(props) =>
		props.$isDraggingOver ? `1px dashed ${transparentize(0.9, '#000')}` : ''};
	background-color: ${(props) =>
		props.$isDraggingOver
			? transparentize(0.95, props.theme.palette.primary.main ?? '')
			: ''};
`;

const LectureDroppable = styled.div<{ $isDraggingOver?: boolean }>`
	/* never 0 high: an empty section must still be a drop target */
	min-height: ${(props) => (props.$isDraggingOver ? '10vh' : '2.5rem')};
	margin: 0.5rem 0 0.5rem 15%;
	padding: ${(props) => (props.$isDraggingOver ? '0.5rem' : '')};
	border-radius: 5px;
	border: ${(props) =>
		props.$isDraggingOver ? `1px dashed ${transparentize(0.9, '#000')}` : ''};
	background-color: ${(props) =>
		props.$isDraggingOver
			? transparentize(0.95, props.theme.palette.orange?.main ?? '')
			: ''};
`;

export default StandaloneCourseCurriculum;
